import { useEffect, useState } from 'react'
import { Button, Popconfirm, Space, Spin, Tabs } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import {
    getPortfolioDetail,
    liquidatePortfolio,
    triggerRebalance,
    triggerRiskCheck,
} from '@/api/portfolio'
import { useFeedback } from '@/utils/antdApp'
import type { PortfolioDetail } from '@/types/portfolio'
import Overview from './components/Overview'
import Holdings from './components/Holdings'
import TradeHistory from './components/TradeHistory'
import AIThinkingLog from './components/AIThinkingLog'
import ConfigSnapshot from './components/ConfigSnapshot'
import TradingWorkspace from './components/TradingWorkspace'
import './index.scss'

const statusMap: Record<number, { label: string; cls: string }> = {
    1: { label: '运营中',  cls: 'active' },
    2: { label: '清仓中',  cls: 'liquidating' },
    3: { label: '观察期',  cls: 'watching' },
    4: { label: '已停盘',  cls: 'archived' },
}

const baseTabItems = [
    { label: '概览',         key: '1' },
    { label: '持仓明细',     key: '2' },
    { label: '交易记录',     key: '3' },
    { label: 'AI 思考记录',  key: '4' },
    { label: '配置信息',     key: '5' },
]

export default function AssetDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { message } = useFeedback()
    const [activeKey, setActiveKey] = useState('1')
    const [detail, setDetail] = useState<PortfolioDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState('')

    const loadDetail = () => {
        if (!id) return
        setLoading(true)
        getPortfolioDetail(Number(id))
            .then(data => setDetail(data))
            .catch(err => message.error(err instanceof Error ? err.message : '加载组合详情失败'))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        loadDetail()
    }, [id])

    const runAction = async (action: 'rebalance' | 'risk' | 'liquidate') => {
        if (!detail) return
        setActionLoading(action)
        try {
            if (action === 'rebalance') {
                await triggerRebalance(detail.id)
                message.success('AI 调仓任务已提交')
            } else if (action === 'risk') {
                await triggerRiskCheck(detail.id)
                message.success('风控检查已提交')
            } else {
                await liquidatePortfolio(detail.id)
                message.success('清仓订单已提交，组合已停盘')
            }
            loadDetail()
        } catch (err) {
            message.error(err instanceof Error ? err.message : '操作失败')
        } finally {
            setActionLoading('')
        }
    }

    if (loading) {
        return (
            <div className="asset-detail">
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin size="large" />
                </div>
            </div>
        )
    }

    if (!detail) {
        return (
            <div className="asset-detail">
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)' }}>
                    组合不存在
                    <br />
                    <Button style={{ marginTop: 16 }} onClick={() => navigate('/asset-mgmt')}>返回列表</Button>
                </div>
            </div>
        )
    }

    const st = statusMap[detail.status] ?? { label: String(detail.status), cls: '' }
    const ret = detail.cumulative_return_pct ?? 0
    const tabItems = detail.trading_portfolio_id
        ? [...baseTabItems, { label: '模拟交易', key: '6' }]
        : baseTabItems

    return (
        <div className="asset-detail">
            <div className="page-header flex flex-between asset-detail__header">
                <div className="asset-detail__title-group">
                    <Button size="small" type="text" className="asset-detail__back"
                        icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset-mgmt')}>
                        全部组合
                    </Button>
                    /
                    <span className="asset-detail__title">{detail.name}</span>
                    <span className={`asset-detail__status-tag asset-detail__status-tag--${st.cls}`}>
                        {st.label}
                    </span>
                    <span className="asset-detail__market-tag">{detail.market}</span>
                    {detail.manager_name && (
                        <span className="asset-detail__manager-hint">
                            {detail.manager_name}
                        </span>
                    )}
                </div>
                <div className="asset-detail__header-right">
                    {detail.status !== 4 && (
                        <Space>
                            <Button size="small" loading={actionLoading === 'risk'} onClick={() => void runAction('risk')}>风控检查</Button>
                            <Button size="small" type="primary" loading={actionLoading === 'rebalance'} onClick={() => void runAction('rebalance')}>AI 调仓</Button>
                            <Popconfirm
                                title="确认清仓并停盘？"
                                description="系统会向模拟交易引擎提交全部持仓的卖出订单。"
                                onConfirm={() => void runAction('liquidate')}
                            >
                                <Button size="small" danger loading={actionLoading === 'liquidate'}>清仓停盘</Button>
                            </Popconfirm>
                        </Space>
                    )}
                    <span className="asset-detail__return-label">累计收益</span>
                    <span className={`asset-detail__return-val ${ret >= 0 ? 'pos' : 'neg'}`}>
                        {ret >= 0 ? '+' : ''}{ret.toFixed(2)}%
                    </span>
                </div>
            </div>

            <div className="page-content asset-detail__content">
                <Tabs activeKey={activeKey} items={tabItems} onChange={setActiveKey} />
                <div className="asset-detail__tab-body">
                    {activeKey === '1' && <Overview detail={detail} />}
                    {activeKey === '2' && (
                        <Holdings
                            portfolioId={detail.id}
                            totalAssets={detail.current_total_assets}
                        />
                    )}
                    {activeKey === '3' && <TradeHistory portfolioId={detail.id} />}
                    {activeKey === '4' && <AIThinkingLog portfolioId={detail.id} />}
                    {activeKey === '5' && <ConfigSnapshot portfolioId={detail.id} />}
                    {activeKey === '6' && detail.trading_portfolio_id && (
                        <TradingWorkspace portfolioId={detail.id} market={detail.market} />
                    )}
                </div>
            </div>
        </div>
    )
}
